/**
 * Reading a receipt photo with the local vision model: the prompt, and turning its reply into
 * a draft you check before anything is added.
 */
/** The JSON shape both prompts ask for. */
const RECEIPT_SHAPE = `{"receipt_count":1,"receipts":[{"store":"Costco","date":"YYYY-MM-DD","mcc":"5300","card_last4":"1234","item_count":12,"items":[{"item_no":"1234567","name":"KS WATER","price":4.99,"coupon":-1.00,"crv":0.00,"taxable":false}],"subtotal":0.00,"tax":0.00,"total":0.00}]}`;

/** The rules after the first one, the same for a scan and for any AI chat. */
const RECEIPT_RULES = `- "item_count": how many product lines that receipt has (count them first; it goes before its "items").
- One entry per product line. Copy item numbers and names as printed.
- A coupon or instant savings line (a minus amount, often with "/" and the product's item number) is not its own item: add it to that product's "coupon" as a negative number.
- A California redemption value line ("CA REDEMP VA", "CRV", a bottle deposit) is not its own item: add its amount to "crv" of the product line just above it.
- "taxable": true when the line is marked as taxed (Costco prints a letter like A or Y next to taxed prices), false when not, null if you can't tell.
- "mcc": the 4-digit merchant category code that fits the store (5300 warehouse club, 5411 grocery, 5812 restaurant, 5814 fast food, 5541 gas station, 5912 pharmacy, 5311 department store), or null.
- Use numbers for money (4.99, not "$4.99"). Use null for anything you can't read.`;

export const RECEIPT_PROMPT = `Read every store receipt in this photo and reply with JSON only, no other text:
${RECEIPT_SHAPE}
Rules:
- "receipt_count": how many separate paper receipts the photo shows (count them first; it goes before "receipts"). Put one entry in "receipts" for each, left to right, then top to bottom. Never merge two receipts into one entry, and never split one receipt into two.
${RECEIPT_RULES}`;

/**
 * The same request for any AI chat (ChatGPT, Claude, Gemini) when LM Studio isn't available:
 * you paste it with your receipt photos, and paste its reply back on the Receipts page.
 */
export const RECEIPT_CHAT_PROMPT = `Read every store receipt in the photos I attach and reply with one JSON code block only, no other text:
${RECEIPT_SHAPE}
Rules:
- "receipt_count": how many separate paper receipts all the photos show together (count them first; it goes before "receipts"). Put one entry in "receipts" for each, photo by photo in the order I attached them, and inside a photo left to right, then top to bottom. One photo can show several receipts, and one long receipt can run across several photos: never merge two receipts into one entry, and never split one receipt into two.
${RECEIPT_RULES}
- Never guess or fill in a line you can't read: use null. If a photo isn't a receipt, leave it out.`;

/**
 * How far the model has got while its reply streams in: how many receipts the photo has (from
 * "receipt_count"), the first store and date once written, how many items are done across all
 * receipts, and how many there are so far (each receipt's "item_count", written before its items).
 */
export function receiptProgress(partial: string): {
  receipts: number | null;
  store: string | null;
  date: string | null;
  done: number;
  total: number | null;
} {
  const store = /"store"\s*:\s*"([^"]{1,40})"/.exec(partial)?.[1] ?? null;
  const date = /"date"\s*:\s*"(\d{4}-\d{2}-\d{2})"/.exec(partial)?.[1] ?? null;
  const receipts = /"receipt_count"\s*:\s*(\d{1,3})/.exec(partial);
  const counts = [...partial.matchAll(/"item_count"\s*:\s*(\d{1,4})/g)].map((m) => Number(m[1]));
  const at = partial.indexOf('"items"');
  // An item is done once its price is written.
  const done = at < 0 ? 0 : (partial.slice(at).match(/"price"\s*:/g) ?? []).length;
  const total = counts.length ? counts.reduce((a, b) => a + b, 0) : null;
  return {
    receipts: receipts && Number(receipts[1]) > 0 ? Number(receipts[1]) : null,
    store,
    date,
    done: total ? Math.min(done, total) : done,
    total: total && total > 0 ? total : null,
  };
}

export interface ReceiptDraftItem {
  itemNo: string;
  name: string;
  priceCents: number;
  couponCents: number;
  /** California redemption value (bottle deposit) printed on its own line after the product. */
  crvCents: number;
  taxable: boolean | null;
}

export interface ReceiptDraft {
  store: string;
  date: string | null;
  mcc: string | null;
  cardLast4: string | null;
  items: ReceiptDraftItem[];
  subtotalCents: number | null;
  taxCents: number | null;
  totalCents: number | null;
}

const cents = (v: unknown): number | null => {
  const n =
    typeof v === "string" ? Number(v.replace(/[$,\s]/g, "")) : typeof v === "number" ? v : NaN;
  return Number.isFinite(n) ? Math.round(n * 100) : null;
};

const str = (v: unknown, max = 80) => (typeof v === "string" ? v.trim().slice(0, max) : "");

/** A redemption value line (Costco prints "CA REDEMP VA" after the bottle it belongs to). */
export function isCrvLine(name: string): boolean {
  return /\bREDEMP|\bCRV\b|BOTTLE DEP/i.test(name);
}

/** The JSON in a reply (an object or a list, whichever starts first), or null. */
function replyJson(text: string): unknown {
  const brace = text.indexOf("{");
  const bracket = text.indexOf("[");
  const list = bracket >= 0 && (brace < 0 || bracket < brace);
  const start = list ? bracket : brace;
  const end = text.lastIndexOf(list ? "]" : "}");
  if (start < 0 || end <= start) return null;
  try {
    return JSON.parse(text.slice(start, end + 1));
  } catch {
    return null;
  }
}

/**
 * Every receipt in the reply, in the order the model listed them (empty when there's no usable
 * JSON). The model is asked for {"receipts": [...]}; a bare list, or one receipt on its own, reads
 * the same way, so a photo with one receipt is simply a list of one.
 */
export function parseReceiptsReply(text: string): ReceiptDraft[] {
  const raw = replyJson(text);
  const list = Array.isArray(raw)
    ? raw
    : raw && typeof raw === "object" && Array.isArray((raw as { receipts?: unknown }).receipts)
      ? (raw as { receipts: unknown[] }).receipts
      : [raw];
  return list
    .filter((r): r is Record<string, unknown> => !!r && typeof r === "object" && !Array.isArray(r))
    .map(readDraft);
}

/** The first receipt in the reply, or null when there's no usable JSON object. */
export function parseReceiptReply(text: string): ReceiptDraft | null {
  return parseReceiptsReply(text)[0] ?? null;
}

/**
 * Receipts pasted from any AI chat's reply (the prompt above, run elsewhere). Reads the reply as a
 * whole first; when that has none, each code block on its own, so a few replies pasted together
 * (or a reply with its JSON split into blocks) still read. Receipts without items are left out,
 * like a scan.
 */
export function pastedReceipts(text: string): ReceiptDraft[] {
  const withItems = (list: ReceiptDraft[]) => list.filter((d) => d.items.length > 0);
  const whole = withItems(parseReceiptsReply(text));
  if (whole.length) return whole;
  const blocks = [...text.matchAll(/```[a-zA-Z]*\s*\n?([\s\S]*?)```/g)].map((m) => m[1] ?? "");
  return withItems(blocks.flatMap((b) => parseReceiptsReply(b)));
}

/** One receipt object from the reply, as a draft. */
function readDraft(raw: Record<string, unknown>): ReceiptDraft {
  const items: ReceiptDraftItem[] = [];
  for (const r of Array.isArray(raw.items) ? raw.items : []) {
    if (!r || typeof r !== "object") continue;
    const row = r as Record<string, unknown>;
    const name = str(row.name);
    const price = cents(row.price);
    if (!name || price === null) continue;
    const coupon = cents(row.coupon);
    // A deposit line the model listed as its own item goes on the product above it.
    const prev = items[items.length - 1];
    if (isCrvLine(name) && prev) {
      prev.crvCents += Math.abs(price);
      continue;
    }
    const crv = cents(row.crv);
    items.push({
      itemNo: str(row.item_no, 40).replace(/\D/g, ""),
      name,
      priceCents: price,
      couponCents: coupon === null ? 0 : -Math.abs(coupon),
      crvCents: crv === null ? 0 : Math.abs(crv),
      taxable: row.taxable === true ? true : row.taxable === false ? false : null,
    });
  }
  const date =
    typeof raw.date === "string" && /^\d{4}-\d{2}-\d{2}$/.test(raw.date) ? raw.date : null;
  const mcc =
    typeof raw.mcc === "string" && /^\d{4}$/.test(raw.mcc)
      ? raw.mcc
      : typeof raw.mcc === "number"
        ? String(raw.mcc).padStart(4, "0")
        : null;
  const last4 =
    typeof raw.card_last4 === "string" && /^\d{4}$/.test(raw.card_last4) ? raw.card_last4 : null;
  return {
    store: str(raw.store) || "Store",
    date,
    mcc: mcc && /^\d{4}$/.test(mcc) ? mcc : null,
    cardLast4: last4,
    items,
    subtotalCents: cents(raw.subtotal),
    taxCents: cents(raw.tax),
    totalCents: cents(raw.total),
  };
}

/** The rows added up, to compare with the printed subtotal. */
export function draftSubtotal(d: ReceiptDraft): number {
  return d.items.reduce((s, i) => s + i.priceCents + i.couponCents + i.crvCents, 0);
}

/** The answer key for a sample receipt (the same shape the model is asked to reply in). */
export interface ReceiptKey {
  store: string;
  date: string;
  card_last4: string;
  items: { item_no: string; name: string; price: number; coupon: number; taxable: boolean }[];
  subtotal: number;
  tax: number;
  total: number;
}

export interface ReadingCheck {
  /** Lines the model found, by item number, out of the lines on the receipt. */
  found: number;
  lines: number;
  prices: number;
  coupons: number;
  taxFlags: number;
  /** Item numbers on the receipt the model missed, and ones it made up or misread. */
  missed: string[];
  extra: string[];
  dateRight: boolean;
  cardRight: boolean;
  totalRight: boolean;
  /** 0–100: lines found with the right price and coupon, plus the date and total. */
  score: number;
}

/** How well a reading matches the answer key, line by line. */
export function checkReading(draft: ReceiptDraft, key: ReceiptKey): ReadingCheck {
  const cents = (n: number) => Math.round(n * 100);
  const byNo = new Map(draft.items.map((i) => [i.itemNo, i]));
  let found = 0;
  let prices = 0;
  let coupons = 0;
  let taxFlags = 0;
  const missed: string[] = [];
  for (const k of key.items) {
    const got = byNo.get(k.item_no);
    if (!got) {
      missed.push(k.item_no);
      continue;
    }
    found++;
    if (got.priceCents === cents(k.price)) prices++;
    if (Math.abs(got.couponCents) === Math.abs(cents(k.coupon))) coupons++;
    if (got.taxable === k.taxable) taxFlags++;
  }
  const keyNos = new Set(key.items.map((k) => k.item_no));
  const extra = draft.items.map((i) => i.itemNo).filter((n) => !keyNos.has(n));
  const dateRight = draft.date === key.date;
  const cardRight = draft.cardLast4 === key.card_last4;
  const totalRight = draft.totalCents === cents(key.total);
  const exact = key.items.filter((k) => {
    const g = byNo.get(k.item_no);
    return (
      g && g.priceCents === cents(k.price) && Math.abs(g.couponCents) === Math.abs(cents(k.coupon))
    );
  }).length;
  const parts = key.items.length + 2;
  const score = Math.round(
    ((exact + (dateRight ? 1 : 0) + (totalRight ? 1 : 0)) / parts) * 100 - extra.length * 2,
  );
  return {
    found,
    lines: key.items.length,
    prices,
    coupons,
    taxFlags,
    missed,
    extra,
    dateRight,
    cardRight,
    totalRight,
    score: Math.max(0, Math.min(100, score)),
  };
}

/**
 * The sales tax rate a receipt shows, in basis points: its printed tax over the taxed rows
 * (price less coupon, plus CRV, which is taxed with its item). Snapped to a quarter percent (California rates, 7.75% = 775) when that
 * gives back the printed tax to within a cent per taxed row; otherwise the exact rate. Null when
 * the receipt has no tax or no row is marked taxed.
 */
export function receiptTaxBp(
  rows: readonly {
    priceCents: number;
    couponCents: number;
    crvCents?: number;
    taxed: boolean | null;
  }[],
  taxCents: number | null,
): number | null {
  if (taxCents === null || taxCents <= 0) return null;
  const taxed = rows.filter((r) => r.taxed === true);
  const base = taxed.reduce(
    (t, r) => t + r.priceCents - Math.abs(r.couponCents) + (r.crvCents ?? 0),
    0,
  );
  if (base <= 0) return null;
  const exact = Math.round((taxCents * 10_000) / base);
  const snapped = Math.round(exact / 25) * 25;
  const off = Math.abs(Math.round((base * snapped) / 10_000) - taxCents);
  return snapped > 0 && off <= taxed.length ? snapped : exact;
}

/**
 * Whether a file's first bytes are a HEIC/HEIF photo (what iPhones save by default). Chrome and
 * most browsers outside Safari can't open these, so the scan converts them first.
 */
export function isHeifHeader(bytes: Uint8Array): boolean {
  if (bytes.length < 12) return false;
  const ascii = (a: number, b: number) => String.fromCharCode(...bytes.subarray(a, b));
  if (ascii(4, 8) !== "ftyp") return false;
  return ["heic", "heix", "hevc", "hevx", "heim", "heis", "mif1", "msf1"].includes(ascii(8, 12));
}
